import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, getDoc, collection, getDocs, query, where, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../Services/Firebase/FirebaseIndex";
import { registerUser, signInWithGoogle, initializeGIS } from "../../Services/Firebase/FirebaseModules/firebaseAuthService";
import { requestEmailVerificationCode, confirmEmailVerificationCode, finalizeVerifiedSignup } from "../../Services/Firebase/FirebaseModules/emailVerificationService";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { AnalyticsService } from "../../Services/Firebase/FirebaseModules/firebaseAnalyticsService";
import { ErrorBoundary } from "react-error-boundary";
import "../../global.css";
import googleButtonImageSU from "../../assets/GoogleButtons/Web (mobile + desktop)/png@4x/light/web_light_sq_SU@4x.png";
import gigifyLogo from "../../assets/Logo/GIGiFY_Logo_Vertical_no_tagline.png";
import heroImage from "../../assets/Images/HeroImage900px.png";
// Import background images
import signupBackground from "../../assets/Backgrounds/Signuploginbackground.jpg";
import metalGreyBackground from "../../assets/Backgrounds/MetalGreybackground2000px.jpg";
import DashboardMobileBackground from "../../assets/Backgrounds/DashboardMobileBackground.jpg";
import { useLoading } from '../../Components/GlobalLoading/LoadingContext';
import { useAuth } from '../../Components/Auth/AuthContext';
import debug from '../../utils/debug';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  useEffect(() => {
    debug.error('ErrorFallback', 'Registration error captured', {
      error: error.message,
      stack: error.stack
    });
    
    Promise.resolve().then(() => {
      AnalyticsService.errorTracking.logError('register_login_error', {
        error: error.message,
        stack: error.stack
      });
    });
  }, [error]);

  return (
    <div style={{
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "0.75rem",
      borderRadius: "15px",
      margin: "0.75rem 0",
      textAlign: "center"
    }}>
      <h3 style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>Something went wrong</h3>
      <p style={{ fontSize: "0.75rem", marginBottom: "1rem" }}>{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        style={{
          backgroundColor: "#01a2f1",
          color: "white",
          border: "none",
          padding: "0.5rem 0.75rem",
          borderRadius: "15px",
          cursor: "pointer",
          fontSize: "0.75rem",
          minWidth: "44px",
          minHeight: "44px"
        }}
      >
        Try again
      </button>
    </div>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { initializeAuth } = useAuth();
  
  const [registerType, setRegisterType] = useState(null);
  const [registrationName, setRegistrationName] = useState("");
  const [registrationSurname, setRegistrationSurname] = useState("");
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [registrationPassword, setRegistrationPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Email verification (6-digit code, before the account is created)
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifyError, setVerifyError] = useState(null);
  const [verifyInfo, setVerifyInfo] = useState(null);

  // Legals + marketing opt-in
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [promotionalEmails, setPromotionalEmails] = useState(false);

  // Account-creation in progress (drives button feedback during the final step)
  const [creating, setCreating] = useState(false);

  // Count down the resend cooldown once a code has been sent
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);
  
  // Mobile state management and scaling
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1000);
  const [isTrueMobile, setIsTrueMobile] = useState(window.innerWidth <= 500);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Calculate scaling factor based on viewport width
  useEffect(() => {
    const calculateScale = () => {
      const width = window.innerWidth;
      if (width <= 1000) {
        setScaleFactor(1); // Mobile layout, no scaling needed
      } else if (width >= 1900) {
        setScaleFactor(1); // Base size at 1900px and above (100%)
      } else {
        // Linear scaling between 1000px (80%) and 1900px (100%)
        // At 1000px: scale = 0.8, at 1900px: scale = 1.0
        const scale = 0.8 + (width - 1000) * 0.2 / (1900 - 1000);
        setScaleFactor(scale);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Hide footer on mount and show it when unmounting
  useEffect(() => {
    // Hide any footer elements
    const footers = document.querySelectorAll('footer, [class*="footer"], [id*="footer"]');
    const headers = document.querySelectorAll('header, [class*="header"], [id*="header"]');
    const navbars = document.querySelectorAll('nav, [class*="navbar"], [class*="nav"]');
    
    footers.forEach(footer => {
      footer.style.display = 'none';
    });
    headers.forEach(header => {
      header.style.display = 'none';
    });
    navbars.forEach(navbar => {
      navbar.style.display = 'none';
    });
    
    // Set body styles to prevent scrolling and ensure full coverage
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100vh';
    document.body.style.width = '100vw';
    
    return () => {
      // Restore footer, header, and navbar visibility when component unmounts
      footers.forEach(footer => {
        footer.style.display = '';
      });
      headers.forEach(header => {
        header.style.display = '';
      });
      navbars.forEach(navbar => {
        navbar.style.display = '';
      });
      
      // Restore body styles
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.height = '';
      document.body.style.width = '';
    };
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1000);
      setIsTrueMobile(window.innerWidth <= 500);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to scale values for desktop
  const scaleValue = useCallback((baseValue, unit = 'px') => {
    if (isMobile) return baseValue + unit; // Don't scale mobile values
    const numValue = parseFloat(baseValue);
    return (numValue * scaleFactor) + unit;
  }, [scaleFactor, isMobile]);

  // Helper function to scale rem values
  const scaleRem = useCallback((baseRem) => {
    if (isMobile) return baseRem + 'rem'; // Don't scale mobile values
    const numValue = parseFloat(baseRem);
    return (numValue * scaleFactor) + 'rem';
  }, [scaleFactor, isMobile]);

  // Memoized event handlers to prevent re-renders
  const handleRegisterTypeChange = useCallback((type) => {
    setRegisterType(type);
  }, []);

  const handleInputChange = useCallback((setter) => (e) => {
    setter(e.target.value);
  }, []);

  const handleWarningClose = useCallback(() => {
    setShowWarning(false);
  }, []);

  // Component lifecycle logging
  useEffect(() => {
    debug.component.mount('Register');
    
    Promise.resolve().then(() => {
      AnalyticsService.trackScreenView('register_page');
    });
    
    initializeGIS().catch(error => {
      debug.error('Register', 'Failed to initialize Google Identity Services', error);
    });
    
    return () => {
      debug.component.unmount('Register');
    };
  }, []);

  const isEmailValid = useCallback((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim()), []);

  // Step 1: email a 6-digit verification code to the entered address
  const handleSendCode = useCallback(async () => {
    setError(null);
    setVerifyError(null);
    setVerifyInfo(null);

    if (!registerType) {
      setShowWarning(true);
      return;
    }
    if (!isEmailValid(registrationEmail)) {
      setVerifyError("Please enter a valid email address.");
      return;
    }

    setSendingCode(true);
    try {
      const result = await requestEmailVerificationCode(registrationEmail.trim().toLowerCase());
      setCodeSent(true);
      setResendCooldown(result?.cooldownSeconds || 60);
      setVerifyInfo("We've emailed you a 6-digit code. Check your inbox (and your spam folder).");
    } catch (err) {
      setVerifyError(err.message || "Could not send the code. Please try again.");
    } finally {
      setSendingCode(false);
    }
  }, [registerType, registrationEmail, isEmailValid]);

  // Step 2: confirm the code the user typed
  const handleVerifyCode = useCallback(async () => {
    setVerifyError(null);
    if (!/^\d{6}$/.test(verificationCode.trim())) {
      setVerifyError("Enter the 6-digit code from your email.");
      return;
    }
    setVerifyingCode(true);
    try {
      await confirmEmailVerificationCode(registrationEmail.trim().toLowerCase(), verificationCode.trim());
      setEmailVerified(true);
      setVerifyInfo("Email verified ✓");
    } catch (err) {
      setVerifyError(err.message || "Could not verify the code. Please try again.");
    } finally {
      setVerifyingCode(false);
    }
  }, [registrationEmail, verificationCode]);

  // Let the user correct a mistyped email and start over
  const handleChangeEmail = useCallback(() => {
    setCodeSent(false);
    setEmailVerified(false);
    setVerificationCode("");
    setResendCooldown(0);
    setVerifyError(null);
    setVerifyInfo(null);
  }, []);

  // Final step: create the (already-verified) account and go straight to the dashboard
  const handleCreate = useCallback(async (e) => {
    e.preventDefault();
    setError(null);

    if (!registerType) {
      setShowWarning(true);
      return;
    }
    if (!emailVerified) {
      setError("Please verify your email address first.");
      return;
    }
    if (!registrationName || !registrationSurname || !registrationPassword) {
      setError("Please fill in all required fields.");
      return;
    }
    if (registrationPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!legalAccepted) {
      setError("Please accept the Terms, Privacy Policy and Community Standards to continue.");
      return;
    }

    setCreating(true);
    setIsLoading(true);
    Promise.resolve().then(() => {
      AnalyticsService.logCustomEvent('registration_attempt', { method: 'email', userType: registerType });
    });

    try {
      await initializeAuth();

      const result = await registerUser(
        registrationEmail.trim().toLowerCase(),
        registrationPassword,
        `${registrationName} ${registrationSurname}`,
        registerType,
        { emailPreVerified: true, legalAccepted: true, promotionalEmails }
      );

      if (!result || !result.user) {
        throw new Error("Registration failed");
      }
      const user = result.user;

      // Flip the auth user's emailVerified flag server-side, then refresh the token
      // so route guards see the verified state immediately (no re-login needed).
      try {
        await finalizeVerifiedSignup();
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await auth.currentUser.getIdToken(true);
        }
      } catch (finalizeErr) {
        debug.error('Register', 'finalizeVerifiedSignup failed', finalizeErr);
      }

      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('registration_success', { method: 'email', userType: registerType });
      });

      const verified = auth.currentUser && auth.currentUser.emailVerified;
      if (verified) {
        debug.log('Register', `User registered as ${registerType}, going straight to dashboard`);
        navigate(`/${registerType.toLowerCase()}-dashboard/${user.uid}`, { replace: true });
      } else {
        // Fallback: verified flag didn't propagate — use the existing verify page
        debug.warn('Register', 'emailVerified not propagated after finalize, falling back to verify-email');
        navigate(`/verify-email?userUID=${user.uid}&type=${registerType}`);
      }
    } catch (error) {
      debug.error('Register', "Registration error", error);
      let errorMessage = "Registration failed";
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = "This email is already registered. Please log in instead";
            break;
          case 'auth/invalid-email':
            errorMessage = "Invalid email format";
            break;
          case 'auth/weak-password':
            errorMessage = "Password is too weak. Please use a stronger password";
            break;
          default:
            errorMessage = error.message || "Registration failed. Please try again";
        }
      } else {
        errorMessage = error.message || "Registration failed. Please try again";
      }
      setError(errorMessage);
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('registration_failure', { method: 'email', reason: errorMessage, userType: registerType });
      });
    } finally {
      setCreating(false);
      setIsLoading(false);
    }
  }, [initializeAuth, registerType, emailVerified, registrationEmail, registrationName, registrationPassword, confirmPassword, registrationSurname, legalAccepted, promotionalEmails, navigate, setIsLoading]);

  const handleGoogleAuth = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    
    debug.log('Register', 'Google authentication attempt started', {
      userType: registerType || 'unknown'
    });
    
    Promise.resolve().then(() => {
      AnalyticsService.logCustomEvent('auth_attempt', {
        method: 'google',
        userType: registerType || 'unknown'
      });
    });
    
    const target = e.currentTarget;
    if (target) {
      target.style.opacity = "0.7";
    }
    
    setIsLoading(true);
    
    try {
      if (!auth) {
        console.error('Firebase auth not available, initializing');
        await initializeAuth();
      }
      
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
        access_type: 'offline'
      });
      
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      if (!user) {
        console.error('Authentication failed: No user returned');
        throw new Error("Google authentication failed");
      }
      
      const userDoc = await getDoc(doc(db, "Users", user.uid));
      let isNewUser = !userDoc.exists();
      let userType = registerType || "Talent";
      
      if (userDoc.exists()) {
        debug.log('Register', 'Existing user found in Firestore');
        userType = userDoc.data()?.profile?.userType || registerType || "Talent";
      } else {
        debug.log('Register', 'Creating new user profile for Google sign-in');
        
        const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const userData = {
          profile: {
            firstName: firstName,
            lastName: lastName,
            email: user.email,
            userType: userType,
            photoURL: user.photoURL || '',
            isVerified: user.emailVerified,
            dateCreated: serverTimestamp(),
            lastUpdated: serverTimestamp()
          }
        };
        
        await setDoc(doc(db, "Users", user.uid), userData);
        debug.log('Register', 'New user profile created');
      }
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('auth_success', {
          method: 'google',
          userType: userType,
          isExistingUser: !isNewUser
        });
      });
      
      debug.log('Register', "Google auth success", {
        userUID: user.uid,
        userType: userType,
        isVerified: user.emailVerified
      });
      
      if (!user.emailVerified) {
        debug.log('Register', "User not verified, redirecting to verification page", {
          emailVerified: user.emailVerified,
          provider: user.providerData?.[0]?.providerId
        });
        
        if (user.providerData?.[0]?.providerId === 'google.com') {
          await user.reload();
          
          if (user.emailVerified) {
            debug.log('Register', "Google user is verified after reload, skipping verification page");
          } else {
            setIsLoading(true);
            navigate(`/verify-email?userUID=${user.uid}&type=${userType}`);
            return;
          }
        } else {
          setIsLoading(true);
          navigate(`/verify-email?userUID=${user.uid}&type=${userType}`);
          return;
        }
      }
      
      if (isNewUser) {
        debug.log('Register', "New user (Google), navigating straight to dashboard");
        const destination = `/${userType.toLowerCase()}-dashboard/${user.uid}`;
        navigate(destination, { replace: true });
      } else {
        debug.log('Register', "Existing user, navigating to dashboard");
        navigate(`/${userType.toLowerCase()}-dashboard/${user.uid}`);
      }
      
    } catch (error) {
      console.error('Google Auth Error:', error);
      let errorMessage = "Authentication failed";
      
      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "Google sign-in was cancelled. Please try again";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "Pop-up blocked by browser. Please allow pop-ups for this site.";
      } else if (error.code) {
        errorMessage = error.message || "Authentication failed. Please try again";
      } else {
        errorMessage = error.message || "Authentication failed. Please try again";
      }
      
      setError(errorMessage);
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('auth_failure', {
          method: 'google',
          reason: errorMessage,
          errorCode: error.code || 'unknown'
        });
      });
    } finally {
      if (target) {
        target.style.opacity = "1";
      }
      setIsLoading(false);
    }
  }, [initializeAuth, registerType, navigate, setIsLoading]);

  // Fixed and enhanced styles for split screen
  const containerStyle = useMemo(() => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    overflow: "hidden",
    backgroundImage: "none", // No background image for container
    backgroundSize: "cover",
    backgroundPosition: "center"
  }), [isMobile]);

  // Left Panel - Hero Image with Signup Background
  const leftPanelStyle = useMemo(() => ({
    width: isMobile ? "100%" : "45%",
    height: "100vh",
    backgroundImage: `url(${signupBackground})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    position: "relative"
  }), [isMobile, signupBackground]);

  // Right Panel - Registration Form with Metal Grey Background
  const rightPanelStyle = useMemo(() => ({
    width: isMobile ? "100%" : "55%",
    height: "100vh",
    backgroundImage: `url(${metalGreyBackground})`, // Same dark background for both mobile and desktop
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    alignItems: "center", // Changed: Always center on desktop, mobile will be handled in container
    justifyContent: "center",
    padding: isMobile ? "1rem" : scaleRem(2.5), // Scaled for desktop
    paddingTop: isMobile ? "20px" : scaleRem(2.5), // Scaled for desktop
    paddingBottom: isMobile ? "80px" : scaleRem(2.5), // Scaled for desktop
    position: "relative",
    overflowY: "auto" // safety scroll so content is never permanently clipped if it slightly overflows
  }), [isMobile, metalGreyBackground, scaleRem]);

  const heroImageStyle = useMemo(() => ({
    maxWidth: "130%",
    height: "auto",
    maxHeight: isMobile ? "400px" : scaleValue(800),
    objectFit: "contain",
    filter: `
      drop-shadow(0 ${scaleValue(15)} ${scaleValue(30)} rgba(0,0,0,0.4))
      drop-shadow(0 ${scaleValue(30)} ${scaleValue(60)} rgba(0,0,0,0.2))
      contrast(1.1)
      saturate(1.15)
      brightness(1.05)
      sharpen(0.3)
    `,
    imageRendering: "high-quality",
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
    perspective: "1000px",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    marginTop: isMobile ? "3rem" : scaleRem(3), // Scaled for desktop
    zIndex: 2,
    willChange: "transform, filter"
  }), [isMobile, scaleValue, scaleRem]);

  const registrationContainerStyle = useMemo(() => ({
    backgroundColor: "rgba(255, 255, 255, 0)", // Transparent for both mobile and desktop
    backgroundImage: "none", // No background image for both
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderRadius: isMobile ? "15px" : scaleValue(15), // Scaled for desktop
    padding: isMobile ? "0.75rem" : `${scaleRem(2)} ${scaleRem(2.5)}`, // Scaled for desktop
    width: "100%",
    maxWidth: isMobile ? "85%" : scaleValue(715), // Scaled for desktop
    height: isMobile ? "auto" : "auto",
    maxHeight: "none", // Size to content so nothing is clipped (was 110vh on desktop, which cut off the footer)
    overflowY: isMobile ? "visible" : "auto",
    margin: isMobile ? "0px 0" : "0",
    boxShadow: isMobile ? `
      0 26px 52px rgba(0, 0, 0, 0.5),
      0 52px 104px rgba(0, 0, 0, 0.4),
      0 78px 156px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.0)
    ` : `
      0 ${scaleValue(26)} ${scaleValue(52)} rgba(0, 0, 0, 0.5),
      0 ${scaleValue(52)} ${scaleValue(104)} rgba(0, 0, 0, 0.4),
      0 ${scaleValue(78)} ${scaleValue(156)} rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.0)
    `,
    backdropFilter: isMobile ? "blur(19.5px)" : `blur(${scaleValue(19.5)})`, // Scaled for desktop
    WebkitBackdropFilter: isMobile ? "blur(19.5px)" : `blur(${scaleValue(19.5)})`, // Scaled for desktop
    transform: isMobile ? "none" : "scale(0.65)", // 35% reduction for non-mobile viewports
    transformOrigin: "center center", // Keep centered when scaling
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden"
  }), [isMobile, scaleValue, scaleRem]);

  const logoContainerStyle = useMemo(() => ({
    marginTop: isMobile ? "0" : scaleRem(0.5), // Scaled for desktop
    marginBottom: isMobile ? (isTrueMobile ? "0.7rem" : "1rem") : "0rem", // Smaller for true mobile
    width: isMobile ? (isTrueMobile ? "120px" : "140px") : scaleValue(250), // Smaller for true mobile
    display: "block",
    margin: isMobile ? (isTrueMobile ? "0 auto 0.7rem auto" : "0 auto 1rem auto") : `${scaleRem(0.5)} auto 0rem auto` // Smaller margins for true mobile
  }), [isMobile, isTrueMobile, scaleValue, scaleRem]);

  const buttonGroupStyle = useMemo(() => ({
    display: "flex",
    flexDirection: "row", // Always row for all viewports
    gap: isMobile ? (isTrueMobile ? "0.2rem" : "0.3rem") : scaleRem(0.65), // Smaller gap for true mobile
    justifyContent: "center",
    marginBottom: isMobile ? (isTrueMobile ? "0.5rem" : "0.75rem") : scaleRem(0.7), // Smaller margin for true mobile
    width: "100%"
  }), [isMobile, isTrueMobile, scaleRem]);

  const formStyle = useMemo(() => ({
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.5), // tighter gap on desktop to fit without scroll
    width: "100%",
  }), [isMobile, isTrueMobile, scaleRem]);

  const inputStyle = useMemo(() => ({
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    border: isMobile ? "2px solid #dadee6" : `${scaleValue(2.6)} solid #dadee6`, // Scaled for desktop
    width: "100%",
    fontSize: isMobile ? (isTrueMobile ? "0.8rem" : "0.9rem") : scaleRem(1.43), // Smaller font for true mobile
    boxSizing: "border-box",
    transition: "border-color 0.3s ease",
    backgroundColor: "transparent",
    boxShadow: isMobile ? "0 2px 4px rgba(0, 0, 0, 0.05)" : `0 ${scaleValue(2.6)} ${scaleValue(5.2)} rgba(0, 0, 0, 0.05)` // Scaled for desktop
  }), [isMobile, isTrueMobile, scaleValue, scaleRem]);

  const submitButtonStyle = useMemo(() => ({
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    backgroundColor: "#01a2f1",
    color: "#fff",
    border: "none",
    fontSize: isMobile ? (isTrueMobile ? "0.95rem" : "1.1rem") : scaleRem(1.43), // Smaller font for true mobile
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontWeight: "500",
    position: "relative",
    overflow: "hidden",
    boxShadow: isMobile ? "0 2px 4px rgba(0, 0, 0, 0.2)" : `0 ${scaleValue(2)} ${scaleValue(4)} rgba(0, 0, 0, 0.2)`,
  }), [isMobile, isTrueMobile, scaleValue, scaleRem]);

  const errorMessageStyle = useMemo(() => ({
    color: "#f85008",
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    marginBottom: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.65), // Smaller margin for true mobile
    fontSize: isMobile ? (isTrueMobile ? "0.95rem" : "1.1rem") : scaleRem(1.43), // Smaller font for true mobile
    textAlign: "center",
  }), [isMobile, isTrueMobile, scaleValue, scaleRem]);

  const modalOverlayStyle = useMemo(() => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "10000"
  }), []);

  const modalContentStyle = useMemo(() => ({
    backgroundColor: "#fff",
    padding: isMobile ? "1.5rem" : scaleRem(2.6), // Scaled for desktop
    borderRadius: isMobile ? "0" : scaleValue(15),
    textAlign: "center",
    maxWidth: isMobile ? "320px" : scaleValue(494), // Scaled for desktop
    width: "90%",
    margin: isMobile ? "0.75rem" : scaleRem(0.975), // Scaled for desktop
  }), [isMobile, scaleValue, scaleRem]);

  const googleButtonStyle = useMemo(() => ({
    marginTop: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.5), // Smaller margin for true mobile
    cursor: "pointer",
    width: isMobile ? (isTrueMobile ? "200px" : "220px") : scaleValue(312), // Smaller for true mobile
    height: "auto",
    transition: "transform 0.2s ease",
    display: "block",
    margin: isMobile ? (isTrueMobile ? "0.4rem auto" : "0.5rem auto") : `${scaleRem(0.5)} auto 0 auto`, // Smaller margins for true mobile
    maxWidth: "90%"
  }), [isMobile, isTrueMobile, scaleValue, scaleRem]);

  // Style for the "Or get started with" divider
  const dividerStyle = useMemo(() => ({
    display: "flex",
    alignItems: "center",
    margin: isMobile ? (isTrueMobile ? "0.4rem 0" : "0.5rem 0") : `${scaleRem(0.8)} 0`, // Smaller margin for true mobile
    color: "#f8f9fa",
    fontSize: isMobile ? (isTrueMobile ? "0.7rem" : "0.8rem") : scaleRem(1.3) // Smaller font for true mobile
  }), [isMobile, isTrueMobile, scaleRem]);

  const dividerLineStyle = useMemo(() => ({
    flex: 1,
    height: "1px",
    backgroundColor: "#f8f9fa",
    margin: "0 0.75rem"
  }), []);

  // Updated marketing text container styles
  const marketingContainerStyles = useMemo(() => ({
    // Main heading container - at the top
    mainHeading: {
      position: "absolute",
      top: "5%",
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      color: "#f8f9fa",
      fontFamily: "Pondar, sans-serif",
      fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
      fontWeight: "bold",
      textShadow: "0 2px 4px rgba(0,0,0,0.5)",
      zIndex: 2,
      width: "100%"
    },
    subtitle: {
      position: "absolute",
      top: "18%", // Increased from 13% to create more space
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      color: "#f8f9fa",
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      textShadow: "0 2px 4px rgba(0,0,0,0.5)",
      maxWidth: "100%",
      zIndex: 2
    },
    // EPK container
    epkContainer: {
      position: "absolute",
      top: "calc(18% - 4rem)", // Moved 4rem further up for maximum spacing
      left: "2%", // Positioned closer to left edge
      width: "32%", // Slightly narrower for better spacing
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(20, 72, 116, 0.12), rgba(20, 72, 116, 0.28), rgba(30, 82, 126, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
    // Organiser container
    organiserContainer: {
      position: "absolute",
      bottom: "8%", // Moved further down for more spacing
      left: "5%", // Moved further left
      width: "28%", // Slightly narrower
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(248, 80, 8, 0.12), rgba(248, 80, 8, 0.28), rgba(258, 90, 18, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
    // Talent container
    talentContainer: {
      position: "absolute",
      bottom: "30%", // Moved down for better spacing
      right: "5%", // Moved further right
      width: "28%", // Slightly narrower
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(1, 162, 241, 0.12), rgba(1, 162, 241, 0.28), rgba(11, 172, 251, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
  }), []);

  // Content text styles
  const textStyles = useMemo(() => ({
    organiserTitle: {
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      fontWeight: "bold",
      color: "#f8f9fa",
      marginBottom: "0.5rem"
    },
    organiserText: {
      fontSize: "clamp(0.7rem, 1.5vw, 0.85rem)",
      color: "#f8f9fa"
    },
    talentTitle: {
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      fontWeight: "bold",
      color: "#f8f9fa",
      marginBottom: "0.5rem"
    },
    talentText: {
      fontSize: "clamp(0.7rem, 1.5vw, 0.85rem)",
      color: "#f8f9fa"
    }
  }), []);

  // Handlers for hover effects
  const handleMouseEnter = useCallback((e, isRegisterType, type) => {
    if (isRegisterType) {
      e.target.style.backgroundColor = "#f85008";
      e.target.style.color = "#dadee6";
      e.target.style.transform = "translateY(-2px)";
      e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
    } else {
      e.target.style.transform = "translateY(-2px)";
      e.target.style.backgroundColor = "#0090d8";
      e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.3)";
    }
  }, []);

  const handleMouseLeave = useCallback((e, isRegisterType, type) => {
    if (isRegisterType) {
      e.target.style.backgroundColor = type === registerType ? "#f85008" : "transparent";
      e.target.style.color = type === registerType ? "#fff" : "#01a2f1";
      e.target.style.transform = "translateY(0)";
      e.target.style.boxShadow = "";
    } else {
      e.target.style.transform = "translateY(0)";
      e.target.style.backgroundColor = "#01a2f1";
      e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
    }
  }, [registerType]);
  
  const handleSubmitMouseDown = useCallback((e) => {
    e.target.style.transform = "translateY(0) scale(0.98)";
    e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.2)";
  }, []);
  
  const handleSubmitMouseUp = useCallback((e) => {
    e.target.style.transform = "translateY(-2px) scale(1)";
    e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.3)";
  }, []);

  const handleGoogleButtonMouseEnter = useCallback((e) => {
    e.target.style.transform = "scale(1.02)";
    e.target.style.filter = "drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15))";
  }, []);

  const handleGoogleButtonMouseLeave = useCallback((e) => {
    e.target.style.transform = "scale(1)";
    e.target.style.filter = "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))";
  }, []);

  const handleInputFocus = useCallback((e) => {
    e.target.style.outline = 'none';
    e.target.style.borderColor = '#dadee6';
    e.target.style.boxShadow = '0 0 0 3px rgba(1, 162, 241, 0.1), 0 4px 8px rgba(0, 0, 0, 0.08)';
  }, []);

  const handleInputBlur = useCallback((e) => {
    e.target.style.borderColor = '#dadee6';
    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <style>{`
        body {
          overflow: hidden !important;
          margin: 0 !important;
          padding: 0 !important;
          height: 100vh !important;
          width: 100vw !important;
        }
        
        footer, [class*="footer"], [id*="footer"],
        header, [class*="header"], [id*="header"],
        nav, [class*="navbar"], [class*="nav"] {
          display: none !important;
        }

        @keyframes gigify-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={containerStyle}>
        {/* Left Panel - Hero Image with Signup Background (Hidden on Mobile) */}
        {!isMobile && (
          <div style={leftPanelStyle}>
            {/* Main heading at the top */}
            <div style={marketingContainerStyles.mainHeading}>
              {/* Heading removed as requested */}
            </div>
        
            
            {/* Hero Image */}
            <img
              src={heroImage}
              alt="Hero"
              style={heroImageStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = `
                  drop-shadow(0 ${scaleValue(20)} ${scaleValue(40)} rgba(0,0,0,0.5))
                  drop-shadow(0 ${scaleValue(40)} ${scaleValue(80)} rgba(0,0,0,0.3))
                  contrast(1.15)
                  saturate(1.25)
                  brightness(1.08)
                  sharpen(0.4)
                `;
                e.currentTarget.style.transform = "translateZ(0) scale(1.02) translateY(-5px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = `
                  drop-shadow(0 ${scaleValue(15)} ${scaleValue(30)} rgba(0,0,0,0.4))
                  drop-shadow(0 ${scaleValue(30)} ${scaleValue(60)} rgba(0,0,0,0.2))
                  contrast(1.1)
                  saturate(1.15)
                  brightness(1.05)
                  sharpen(0.3)
                `;
                e.currentTarget.style.transform = "translateZ(0) scale(1) translateY(0)";
              }}
           />
           
           {/* EPK container */}
           <div
             style={marketingContainerStyles.epkContainer}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
               e.currentTarget.style.boxShadow = `
                 0 35px 80px rgba(0,0,0,0.8),
                 0 18px 40px rgba(0,0,0,0.6),
                 0 8px 20px rgba(0,0,0,0.5),
                 0 3px 10px rgba(0,0,0,0.4),
                 inset 0 3px 0 rgba(255,255,255,0.5),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.3),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
               e.currentTarget.style.boxShadow = `
                 0 25px 60px rgba(0,0,0,0.7),
                 0 12px 30px rgba(0,0,0,0.5),
                 0 6px 15px rgba(0,0,0,0.4),
                 0 2px 8px rgba(0,0,0,0.3),
                 inset 0 3px 0 rgba(255,255,255,0.4),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.25),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
             }}
           >
             <strong>GIGiFY</strong> helps you create a professional{" "}
             <span style={{color: "#01a2f1", fontWeight: "bold"}}>Digital Talent Showcase</span>{" "}
             that highlights your skills and experience connecting you with top organisers
           </div>

           {/* Organiser container */}
           <div
             style={marketingContainerStyles.organiserContainer}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
               e.currentTarget.style.boxShadow = `
                 0 35px 80px rgba(0,0,0,0.8),
                 0 18px 40px rgba(0,0,0,0.6),
                 0 8px 20px rgba(0,0,0,0.5),
                 0 3px 10px rgba(0,0,0,0.4),
                 inset 0 3px 0 rgba(255,255,255,0.5),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.3),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
               e.currentTarget.style.boxShadow = `
                 0 25px 60px rgba(0,0,0,0.7),
                 0 12px 30px rgba(0,0,0,0.5),
                 0 6px 15px rgba(0,0,0,0.4),
                 0 2px 8px rgba(0,0,0,0.3),
                 inset 0 3px 0 rgba(255,255,255,0.4),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.25),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
             }}
           >
             <div style={textStyles.organiserTitle}>Organiser?</div>
             <div style={textStyles.organiserText}>
               Find your event's secret sauce and{" "}
               <span style={{color: "#f8f9fa", fontWeight: "bold"}}>book talent</span>{" "}
               smoother than a jazz.
             </div>
           </div>

           {/* Talent container */}
           <div
             style={marketingContainerStyles.talentContainer}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
               e.currentTarget.style.boxShadow = `
                 0 35px 80px rgba(0,0,0,0.8),
                 0 18px 40px rgba(0,0,0,0.6),
                 0 8px 20px rgba(0,0,0,0.5),
                 0 3px 10px rgba(0,0,0,0.4),
                 inset 0 3px 0 rgba(255,255,255,0.5),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.3),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
               e.currentTarget.style.boxShadow = `
                 0 25px 60px rgba(0,0,0,0.7),
                 0 12px 30px rgba(0,0,0,0.5),
                 0 6px 15px rgba(0,0,0,0.4),
                 0 2px 8px rgba(0,0,0,0.3),
                 inset 0 3px 0 rgba(255,255,255,0.4),
                 inset 0 -3px 0 rgba(0,0,0,0.2),
                 inset 2px 0 0 rgba(255,255,255,0.25),
                 inset -2px 0 0 rgba(0,0,0,0.15)
               `;
               e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
             }}
           >
             <div style={textStyles.talentTitle}>Talent?</div>
             <div style={textStyles.talentText}>
               Get{" "}
               <span style={{color: "#f8f9fa", fontWeight: "bold"}}>discovered</span>{" "}
               faster than a viral cat video and turn those skills into bills.
             </div>
           </div>
         </div>
       )}
       
       {/* Right Panel - Registration Form with Metal Grey Background */}
       <div style={rightPanelStyle}>
         <div style={registrationContainerStyle}>
           <div style={{ position: "relative", zIndex: 2 }}>
             <div style={logoContainerStyle}>
               <Link to="/">
                 <img
                   src={gigifyLogo}
                   alt="GIGiFY"
                   style={{
                     width: "100%",
                     height: "auto",
                     transition: "transform 0.3s ease"
                   }}
                   onMouseEnter={handleGoogleButtonMouseEnter}
                   onMouseLeave={handleGoogleButtonMouseLeave}
                 />
               </Link>
             </div>
             
 
            <p style={{
              marginTop: isMobile ? (isTrueMobile ? "1rem" : "1.5rem") : scaleRem(0.8), // tighter on desktop to fit without scroll
              marginBottom: isMobile ? (isTrueMobile ? "1rem" : "1.5rem") : scaleRem(0.8), // tighter on desktop to fit without scroll
              fontSize: isMobile ? (isTrueMobile ? "0.65rem" : "0.75rem") : scaleRem(1.105), // Smaller for true mobile
              color: "#f8f9fa",
              textAlign: "center"
            }}>
              First select the type of account you wish to register
            </p>
             
             <div style={buttonGroupStyle}>
               <button
                 style={{
                   backgroundColor: registerType === "Talent" ? "#f85008" : "transparent",
                   color: registerType === "Talent" ? "#fff" : "#01a2f1",
                   border: isMobile ? "2px solid #01a2f1" : `${scaleValue(2.6)} solid #01a2f1`, // Scaled for desktop
                   padding: isMobile ? (isTrueMobile ? "0.4rem 0.3rem" : "0.5rem 0.4rem") : `${scaleRem(0.78)} ${scaleRem(1.3)}`, // Smaller padding for true mobile
                   borderRadius: "15px",
                   fontSize: isMobile ? (isTrueMobile ? "0.75rem" : "0.9rem") : scaleRem(1.495), // Smaller font for true mobile
                   cursor: "pointer",
                   transition: "all 0.3s ease",
                   fontWeight: "500",
                   width: "100%"
                 }}
                 onClick={() => handleRegisterTypeChange("Talent")}
                 onMouseEnter={(e) => handleMouseEnter(e, true)}
                 onMouseLeave={(e) => handleMouseLeave(e, true, "Talent")}
                 onMouseDown={(e) => (e.target.style.transform = "scale(0.95)")}
                 onMouseUp={(e) => (e.target.style.transform = "scale(1)")}
               >
                 Register Talent
               </button>
               <button
                 style={{
                   backgroundColor: registerType === "Organiser" ? "#f85008" : "transparent",
                   color: registerType === "Organiser" ? "#fff" : "#01a2f1",
                   border: isMobile ? "2px solid #01a2f1" : `${scaleValue(2.6)} solid #01a2f1`, // Scaled for desktop
                   padding: isMobile ? (isTrueMobile ? "0.4rem 0.3rem" : "0.5rem 0.4rem") : `${scaleRem(0.78)} ${scaleRem(1.3)}`, // Smaller padding for true mobile
                   borderRadius: "15px",
                   fontSize: isMobile ? (isTrueMobile ? "0.75rem" : "0.9rem") : scaleRem(1.495), // Smaller font for true mobile
                   cursor: "pointer",
                   transition: "all 0.3s ease",
                   fontWeight: "500",
                   width: "100%"
                 }}
                 onClick={() => handleRegisterTypeChange("Organiser")}
                 onMouseEnter={(e) => handleMouseEnter(e, true)}
                 onMouseLeave={(e) => handleMouseLeave(e, true, "Organiser")}
                 onMouseDown={(e) => (e.target.style.transform = "scale(0.95)")}
                 onMouseUp={(e) => (e.target.style.transform = "scale(1)")}
               >
                 Register Organiser
               </button>
             </div>
             
             {error && (
               <div style={errorMessageStyle}>
                 {error}
               </div>
             )}
             
             <form
               onSubmit={handleCreate}
               style={formStyle}
               autoComplete="off" 
             >
               <input
                 type="text"
                 style={{display: 'none'}}
                 name="prevent_autofill"
                 id="prevent_autofill_register" 
                 defaultValue=""
               />
               {/* Step 1 — verify email before anything else */}
               <input
                 type="email"
                 name="register-email"
                 placeholder="Email Address"
                 value={registrationEmail}
                 onChange={handleInputChange(setRegistrationEmail)}
                 disabled={codeSent}
                 style={{
                   ...inputStyle,
                   color: "#f8f9fa",
                   opacity: codeSent ? 0.7 : 1,
                   "::placeholder": { color: "#f8f9fa" }
                 }}
                 required
                 autoComplete="new-password"
                 onFocus={handleInputFocus}
                 onBlur={handleInputBlur}
               />

               {!codeSent && !emailVerified && (
                 <button
                   type="button"
                   onClick={handleSendCode}
                   disabled={sendingCode}
                   style={{ ...submitButtonStyle, opacity: sendingCode ? 0.7 : 1 }}
                 >
                   {sendingCode ? "Sending..." : "Send verification code"}
                 </button>
               )}

               {codeSent && !emailVerified && (
                 <>
                   <input
                     type="text"
                     inputMode="numeric"
                     maxLength={6}
                     name="verification-code"
                     placeholder="6-digit code"
                     value={verificationCode}
                     onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                     style={{ ...inputStyle, color: "#f8f9fa", letterSpacing: "0.3rem", textAlign: "center" }}
                     autoComplete="one-time-code"
                     onFocus={handleInputFocus}
                     onBlur={handleInputBlur}
                   />
                   <button
                     type="button"
                     onClick={handleVerifyCode}
                     disabled={verifyingCode}
                     style={{ ...submitButtonStyle, opacity: verifyingCode ? 0.7 : 1 }}
                   >
                     {verifyingCode ? "Verifying..." : "Verify code"}
                   </button>
                   <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                     <button
                       type="button"
                       onClick={handleSendCode}
                       disabled={resendCooldown > 0 || sendingCode}
                       style={{ background: "none", border: "none", padding: 0, fontSize: "0.85rem", cursor: resendCooldown > 0 ? "default" : "pointer", color: resendCooldown > 0 ? "#9aa4b2" : "#01a2f1" }}
                     >
                       {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                     </button>
                     <button
                       type="button"
                       onClick={handleChangeEmail}
                       style={{ background: "none", border: "none", padding: 0, fontSize: "0.85rem", cursor: "pointer", color: "#01a2f1" }}
                     >
                       Use a different email
                     </button>
                   </div>
                 </>
               )}

               {verifyInfo && (
                 <div style={{ color: emailVerified ? "#1fbf75" : "#f8f9fa", fontWeight: emailVerified ? 700 : 400, fontSize: emailVerified ? "1.7rem" : "0.85rem", textAlign: "center" }}>{verifyInfo}</div>
               )}
               {verifyError && (
                 <div style={{ color: "#f85008", fontSize: "0.85rem", textAlign: "center" }}>{verifyError}</div>
               )}

               {/* Step 2 — the rest, revealed once the email is verified */}
               {emailVerified && (
                 <>
                   <input
                     type="text"
                     name="register-name"
                     placeholder="Name"
                     value={registrationName}
                     onChange={handleInputChange(setRegistrationName)}
                     style={{ ...inputStyle, color: "#f8f9fa", "::placeholder": { color: "#f8f9fa" } }}
                     required
                     autoComplete="new-password"
                     onFocus={handleInputFocus}
                     onBlur={handleInputBlur}
                   />
                   <input
                     type="text"
                     name="register-surname"
                     placeholder="Surname"
                     value={registrationSurname}
                     onChange={handleInputChange(setRegistrationSurname)}
                     style={{ ...inputStyle, color: "#f8f9fa", "::placeholder": { color: "#f8f9fa" } }}
                     required
                     autoComplete="new-password"
                     onFocus={handleInputFocus}
                     onBlur={handleInputBlur}
                   />
                   <div style={{ position: 'relative', width: '100%' }}>
                     <input
                       type={showPassword ? "text" : "password"}
                       name="register-password"
                       placeholder="Password"
                       value={registrationPassword}
                       onChange={handleInputChange(setRegistrationPassword)}
                       style={{
                         ...inputStyle,
                         color: "#f8f9fa",
                         "::placeholder": { color: "#f8f9fa" },
                         paddingRight: isMobile ? (isTrueMobile ? "2.2rem" : "2.5rem") : scaleRem(3.25)
                       }}
                       required
                       autoComplete="new-password"
                       onFocus={handleInputFocus}
                       onBlur={handleInputBlur}
                     />
                     <button
                       type="button"
                       onClick={() => setShowPassword(!showPassword)}
                       style={{
                         position: 'absolute',
                         right: isMobile ? (isTrueMobile ? '0.5rem' : '0.75rem') : scaleRem(0.975),
                         top: '50%',
                         transform: 'translateY(-50%)',
                         background: 'none',
                         border: 'none',
                         cursor: 'pointer',
                         color: '#01a2f1',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         padding: '0.25rem',
                         borderRadius: '4px',
                         transition: 'color 0.2s ease',
                         minWidth: '44px',
                         minHeight: '44px'
                       }}
                       onMouseEnter={(e) => e.target.style.color = '#01a2f1'}
                       onMouseLeave={(e) => e.target.style.color = '#01a2f1'}
                       aria-label={showPassword ? "Hide password" : "Show password"}
                     >
                       {showPassword ? <EyeOff size={25} /> : <Eye size={25} />}
                     </button>
                   </div>
                   <input
                     type={showPassword ? "text" : "password"}
                     name="confirm-password"
                     placeholder="Confirm Password"
                     value={confirmPassword}
                     onChange={handleInputChange(setConfirmPassword)}
                     style={{ ...inputStyle, color: "#f8f9fa", "::placeholder": { color: "#f8f9fa" } }}
                     required
                     autoComplete="new-password"
                     onFocus={handleInputFocus}
                     onBlur={handleInputBlur}
                   />

                   <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", color: "#f8f9fa", fontSize: "1.6rem", textAlign: "left", lineHeight: 1.4 }}>
                     <input type="checkbox" checked={legalAccepted} onChange={(e) => setLegalAccepted(e.target.checked)} style={{ marginTop: "0.2rem", minWidth: "16px", minHeight: "16px" }} />
                     <span>
                       I accept the{" "}
                       <Link to="/service-terms" target="_blank" rel="noopener noreferrer" style={{ color: "#01a2f1", textDecoration: "underline" }}>Terms</Link>,{" "}
                       <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#01a2f1", textDecoration: "underline" }}>Privacy Policy</Link>{" "}and{" "}
                       <Link to="/community-standards" target="_blank" rel="noopener noreferrer" style={{ color: "#01a2f1", textDecoration: "underline" }}>Community Standards</Link>.
                     </span>
                   </label>
                   <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", color: "#f8f9fa", fontSize: "1.6rem", textAlign: "left", lineHeight: 1.4 }}>
                     <input type="checkbox" checked={promotionalEmails} onChange={(e) => setPromotionalEmails(e.target.checked)} style={{ marginTop: "0.2rem", minWidth: "16px", minHeight: "16px" }} />
                     <span>Send me updates, news and event highlights.</span>
                   </label>

                   <button
                     type="submit"
                     disabled={creating}
                     style={{ ...submitButtonStyle, opacity: creating ? 0.7 : 1, cursor: creating ? "default" : "pointer" }}
                     onMouseEnter={(e) => !creating && handleMouseEnter(e, false)}
                     onMouseLeave={(e) => !creating && handleMouseLeave(e, false)}
                     onMouseDown={creating ? undefined : handleSubmitMouseDown}
                     onMouseUp={creating ? undefined : handleSubmitMouseUp}
                   >
                     {creating ? (
                       <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                         <Loader2 size={18} style={{ animation: "gigify-spin 0.8s linear infinite" }} />
                         Creating your account…
                       </span>
                     ) : "Create account"}
                   </button>
                 </>
               )}
             </form>

             <div id="googleSignInButton" style={{ display: 'none' }}></div>
             
             {/* "or get started with" divider */}
             <div style={dividerStyle}>
               <div style={dividerLineStyle}></div>
               <span>or</span>
               <div style={dividerLineStyle}></div>
             </div>
             
             <img
               src={googleButtonImageSU}
               alt="Google Sign Up"
               onClick={handleGoogleAuth}
               style={googleButtonStyle}
               onMouseEnter={handleGoogleButtonMouseEnter}
               onMouseLeave={handleGoogleButtonMouseLeave}
             />
             
             <p style={{ 
              marginTop: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.6), // Smaller for true mobile
              marginBottom: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.5), // Smaller for true mobile
              fontSize: isMobile ? (isTrueMobile ? "0.65rem" : "0.75rem") : scaleRem(1.105), // Smaller for true mobile
              color: "#f8f9fa",
              textAlign: "center"
            }}>
              Already have an account?{" "}
              <Link 
                to="/login" 
                style={{ 
                  color: "#01a2f1", 
                  textDecoration: "none",
                  fontWeight: "500"
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                onMouseLeave={(e) => e.target.style.textDecoration = "none"}
              >
                Login here
              </Link>
            </p>
           </div>
         </div>
       </div>
     </div>
     
     {showWarning && (
       <div style={modalOverlayStyle}>
         <div style={modalContentStyle}>
           <h3 style={{ marginBottom: "0.5rem", color: "#f85008", fontSize: isMobile ? "0.9rem" : "1.3rem" }}>
             Registration Incomplete
           </h3>
           <p style={{ marginBottom: "0.75rem", color: "#144874", fontSize: isMobile ? "0.8rem" : "1.105rem" }}>
             Please select the type of account before proceeding.
           </p>
           <button
             onClick={handleWarningClose}
             style={{
               padding: isMobile ? "0.4rem 0.75rem" : "0.65rem 1.3rem", // Increased by 30% for desktop
               backgroundColor: "#01a2f1",
               color: "#fff",
               border: "none",
               borderRadius: "0",
               cursor: "pointer",
               transition: "all 0.3s ease",
               fontSize: isMobile ? "0.8rem" : "1.105rem", // Increased by 30% for desktop
               fontWeight: "500",
               minWidth: isMobile ? "44px" : "57px", // Increased by 30% for desktop
               minHeight: isMobile ? "44px" : "57px" // Increased by 30% for desktop
             }}
             onMouseEnter={(e) => {
               e.target.style.backgroundColor = "#005b94";
               e.target.style.transform = "scale(1.05)";
             }}
             onMouseLeave={(e) => {
               e.target.style.backgroundColor = "#01a2f1";
               e.target.style.transform = "scale(1)";
             }}
           >
             OK
           </button>
         </div>
       </div>
     )}
   </ErrorBoundary>
 );
};

export default Register;